"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useRouter, usePathname } from "next/navigation";

type NavigationProps = {
  offsetTop?: number;
};

export default function Navigation({ offsetTop = 0 }: Readonly<NavigationProps>) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  const toggleMobileMenu = () =>
    setIsMobileMenuOpen((previousValue) => {
      if (!previousValue) {
        lastFocusedElementRef.current = document.activeElement as HTMLElement | null;
      }
      return !previousValue;
    });
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Handle hash links that need to navigate to home page first
  const scrollToElement = (hash: string, attempts = 0) => {
    const element = document.getElementById(hash);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (attempts < 50) {
      setTimeout(() => scrollToElement(hash, attempts + 1), 50);
    }
  };

  const handleHashLink = (
    e: React.MouseEvent<HTMLAnchorElement>,
    hash: string,
  ) => {
    e.preventDefault();
    closeMobileMenu();

    if (pathname === "/") {
      scrollToElement(hash);
    } else {
      router.push("/");
      setTimeout(() => scrollToElement(hash), 100);
    }
  };

  // Lock body scroll + ESC close + basic focus trap for the mobile modal
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    setTimeout(() => closeBtnRef.current?.focus(), 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMobileMenu();
        return;
      }

      if (e.key !== "Tab") return;
      const root = panelRef.current;
      if (!root) return;

      const focusable = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), summary, textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (!active || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    globalThis.addEventListener("keydown", onKeyDown);
    return () => {
      globalThis.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen || !lastFocusedElementRef.current) return;
    lastFocusedElementRef.current.focus();
    lastFocusedElementRef.current = null;
  }, [isMobileMenuOpen]);

  const mobileItemBase =
    "flex items-center justify-between w-full rounded-xl px-4 py-4 text-base font-medium text-white/90 " +
    "transition-colors hover:text-gel-400 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-gel-400/60";

  const mobileSubItemBase =
    "block rounded-lg px-4 py-3 text-sm text-white/75 transition-colors hover:text-gel-400 hover:bg-white/5";

  const groupLabel =
    "px-4 pt-3 pb-2 text-[11px] uppercase tracking-wider text-white/55";
  const divider = "my-3 h-px bg-white/10";

  const overlayDialogClasses =
    "fixed inset-0 z-[70] m-0 h-screen w-screen border-0 bg-transparent p-0";

  const overlayBackdropClasses =
    "absolute inset-0 " +
    "bg-[radial-gradient(1000px_circle_at_50%_-10%,rgba(99,102,241,.22),transparent_60%),radial-gradient(900px_circle_at_90%_10%,rgba(236,72,153,.15),transparent_55%),linear-gradient(to_bottom,rgba(0,0,0,.35),rgba(0,0,0,.55))] " +
    "backdrop-blur-sm transition-opacity duration-200";

  // âœ… FIXED: clean, valid Tailwind string (no missing spaces)
  const panelClasses =
    "relative w-[min(92vw,440px)] rounded-2xl " +
    "bg-black/45 backdrop-blur-2xl " +
    "border border-white/50 " +
    "shadow-[0_30px_90px_rgba(0,0,0,0.25)] " +
    "max-h-[calc(100dvh-1.5rem)] overflow-hidden " +
    "opacity-0 scale-95 blur-[2px] animate-[menu-focus_280ms_ease-out_forwards]";

  // âœ… Ambient glow (not a full-panel tint)
  const panelGlow =
    "pointer-events-none absolute -inset-10 opacity-80 " +
    "bg-[radial-gradient(600px_circle_at_55%_28%,rgba(59,130,246,0.18),transparent_55%),radial-gradient(520px_circle_at_42%_88%,rgba(236,72,153,0.14),transparent_60%)]";

  const closeBtn =
    "inline-flex h-10 w-10 items-center justify-center rounded-full " +
    "bg-white/5 hover:bg-white/10 text-white/85 hover:text-white " +
    "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gel-400/60";

  const glowLine =
  "pointer-events-none relative h-px w-full " +
  "bg-gradient-to-r from-transparent to-transparent " +
  // super hot center hotspot
  "after:absolute after:-top-3 after:left-1/2 after:h-6 after:w-[70%] after:-translate-x-1/2 " +
  "after:bg-[radial-gradient(circle_at_50%_50%,rgba(56,189,248,0.95),rgba(34,211,238,0.55),transparent_70%)] " +
  "after:blur-xl after:opacity-100 " +
  // extra spill
  "before:absolute before:-top-10 before:left-1/2 before:h-20 before:w-[95%] before:-translate-x-1/2 " +
  "before:bg-[radial-gradient(circle_at_50%_50%,rgba(34,211,238,0.55),transparent_70%)] " +
  "before:blur-3xl before:opacity-100";

  return (
    <>
      <nav
        className="fixed top-0 w-full z-40 bg-black/30 backdrop-blur-md border-b border-white/10"
        style={offsetTop ? { top: `${offsetTop}px` } : undefined}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Image
                src="/icon.svg"
                alt="Rain Block® Logo"
                width={40}
                height={40}
                style={{ margin: "-3px" }}
              />
              <Link href="/" className="text-2xl font-bold text-white">
                Rain Block<sup className="tm-reg">&reg;</sup>
              </Link>
            </div>

            {/* Desktop Menu (unchanged) */}
            <div className="hidden min-[1025px]:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link
                  href="/"
                  className="text-white/90 hover:text-gel-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Home
                </Link>
                <Link
                  href="/#features"
                  onClick={(e) => handleHashLink(e, "features")}
                  className="text-white/90 hover:text-gel-400 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer"
                  scroll={false}
                >
                  Features
                </Link>
                <Link
                  href="/#reviews"
                  onClick={(e) => handleHashLink(e, "reviews")}
                  className="text-white/90 hover:text-gel-400 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer"
                  scroll={false}
                >
                  Reviews
                </Link>

                <div className="relative group">
                  <Link
                    href="/shop"
                    className="rounded-full border border-white/20 bg-white/10 px-5 py-2 text-sm font-bold text-white hover:bg-white/15 transition-colors flex items-center focus:outline-none"
                  >
                    Shop
                    <svg
                      className="ml-1 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Link>
                  <div className="absolute left-0 top-full pt-2 w-40 opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity z-50">
                    <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-md shadow-lg">
                      <Link
                        href="/shop"
                        className="block px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-t-md"
                      >
                        Shop
                      </Link>
                      <Link
                        href="/shop-bulk"
                        className="block px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-b-md"
                      >
                        Bulk
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <button className="text-white/90 hover:text-gel-400 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center focus:outline-none">
                    Guides
                    <svg
                      className="ml-1 h-4 w-4 group-hover:translate-x-0.5 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                  <div className="absolute left-0 top-full pt-2 w-72 opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity z-50">
                    <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-md shadow-lg">
                      <Link
                        href="/guides/best-weatherproof-extension-cords"
                        className="block whitespace-nowrap px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-t-md"
                      >
                        Buying Guide
                      </Link>
                      <Link
                        href="/guides/rainblock-vs-twist-seal"
                        className="block whitespace-nowrap px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5"
                      >
                        Rain Block® vs Competitors
                      </Link>
                      <Link
                        href="/guides/how-to-weatherproof-christmas-lights-and-decorations"
                        className="block whitespace-nowrap px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5"
                      >
                        How-To Guide
                      </Link>
                      <Link
                        href="/guides/christmas-light-studio-instructions"
                        className="flex items-center gap-2 whitespace-nowrap px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-b-md"
                      >
                        <Image
                          src="/sparkle.svg"
                          alt=""
                          width={16}
                          height={16}
                          aria-hidden
                        />
                        <span>Design Studio Instructions</span>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <button className="text-white/90 hover:text-gel-400 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center focus:outline-none">
                    Help
                    <svg
                      className="ml-1 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div className="absolute left-0 top-full pt-2 w-48 opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity z-50">
                    <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-md shadow-lg">
                      <Link
                        href="/support"
                        className="block px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-t-md"
                      >
                        Support
                      </Link>
                      <Link
                        href="/contact"
                        className="block px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5"
                      >
                        Contact Us
                      </Link>
                      <Link
                        href="/faq"
                        className="block px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-b-md"
                      >
                        FAQ
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <button className="group relative top-[5px] inline-flex items-center gap-2 text-white/90 hover:text-gel-400 px-3 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none">
                    <Image
                      src="/sparkle.svg"
                      alt=""
                      width={24}
                      height={24}
                      aria-hidden
                      className="transition-transform duration-200 group-hover:scale-110"
                    />
                    Design Studio
                    <svg
                      className="h-4 w-4 transition-transform group-hover:translate-y-0.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div className="absolute right-0 top-full pt-2 w-56 opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity z-50">
                    <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-md shadow-lg">
                      <Link
                        href="/guides/christmas-light-studio-instructions"
                        className="block px-4 py-2 text-white/90 hover:text-gel-400 hover:bg-white/5 rounded-t-md"
                      >
                        Instructions
                      </Link>
                      <Link
                        href="https://software.rainblock.net"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="m-2 flex items-center justify-center rounded-md btn-cta px-4 py-2 text-sm font-extrabold text-white transition-colors"
                      >
                        Get Creative Now
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="min-[1025px]:hidden">
              <button
                onClick={toggleMobileMenu}
                className="text-white/90 hover:text-gel-400 p-2 rounded-md transition-colors"
                aria-label="Toggle menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <dialog
          open
          className={overlayDialogClasses}
          aria-labelledby="mobile-menu-title"
          onCancel={(event) => {
            event.preventDefault();
            closeMobileMenu();
          }}
        >
          <button
            type="button"
            aria-label="Close menu backdrop"
            onClick={closeMobileMenu}
            className={overlayBackdropClasses}
          />
          <div className="relative z-10 flex h-full w-full items-center justify-center p-3">
            <div ref={panelRef} className={panelClasses}>
            <h2 id="mobile-menu-title" className="sr-only">
              Mobile menu
            </h2>
            {/* Ambient glow */}
            <div className={panelGlow} />

            {/* Thin glass rim (ONE) */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl border border-white/10 opacity-70" />
            <div className="pointer-events-none absolute inset-[1px] rounded-[15px] border border-white/8 opacity-50" />

            {/* Single cinematic sheen (subtle + slow) */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
              <div className="absolute -top-28 left-[-55%] h-72 w-[210%] rotate-6 bg-gradient-to-r from-transparent via-white/8 to-transparent blur-xl opacity-40 animate-[sheen_9s_ease-in-out_infinite]" />
            </div>

            {/* Top bar */}
            <div className="relative z-10 px-4 pb-2">
              <div className={`${glowLine} glow_sweep`} />
              <div className="text-[11px] uppercase tracking-wider text-white/55">
                Menu
              </div>
              <button
                ref={closeBtnRef}
                onClick={closeMobileMenu}
                className={closeBtn}
                aria-label="Close menu"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="relative z-10 max-h-[calc(100dvh-8.5rem)] overflow-y-auto overscroll-contain px-2 pb-3">
              <div className={groupLabel}>Primary</div>

              <Link
                href="/shop"
                onClick={closeMobileMenu}
                className={
                  mobileItemBase + " font-semibold bg-white/5 hover:bg-white/10"
                }
              >
                <span>Shop</span>
              </Link>
              <div className="mt-1 mb-2 mx-1 rounded-xl bg-black/30 border border-white/10 overflow-hidden">
                <Link
                  href="/shop-bulk"
                  onClick={closeMobileMenu}
                  className={mobileSubItemBase}
                >
                  Bulk
                </Link>
              </div>

              <details className="group mt-2 rounded-xl">
                <summary
                  className="group relative flex items-center justify-between w-full rounded-xl px-4 py-4 text-base font-semibold text-white
                    bg-white/6 hover:bg-white/10 border border-white/10 hover:border-white/20
                    transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gel-400/60 select-none cursor-pointer"
                >
                  <span className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-r from-gel-400/14 via-blue-400/10 to-transparent opacity-70 blur-md" />
                  <span className="relative inline-flex items-center gap-3">
                    <Image
                      src="/sparkle.svg"
                      alt=""
                      width={22}
                      height={22}
                      aria-hidden
                      className="transition-transform duration-200 group-hover:scale-110"
                    />
                    <span>Design Studio</span>
                  </span>
                  <svg
                    className="relative h-4 w-4 text-white/70 transition-transform group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <div className="mt-1 mb-2 mx-1 rounded-xl bg-black/30 border border-white/10 overflow-hidden">
                  <Link
                    href="/guides/christmas-light-studio-instructions"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    Instructions
                  </Link>
                  <Link
                    href="https://software.rainblock.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                    className="m-2 flex items-center justify-center rounded-lg btn-cta px-4 py-3 text-sm font-extrabold text-white transition-colors"
                  >
                    Get Creative Now
                  </Link>
                </div>
              </details>
              <div className={`${glowLine} glow_sweep`} />
              <div className={divider} />

              <div className={groupLabel}>Explore</div>

              <Link
                href="/"
                onClick={closeMobileMenu}
                className={mobileItemBase}
              >
                Home
              </Link>

              <Link
                href="/#features"
                onClick={(e) => handleHashLink(e, "features")}
                className={mobileItemBase + " cursor-pointer"}
                scroll={false}
              >
                Features
              </Link>

              <Link
                href="/#reviews"
                onClick={(e) => handleHashLink(e, "reviews")}
                className={mobileItemBase + " cursor-pointer"}
                scroll={false}
              >
                Reviews
              </Link>

              <details className="group rounded-xl">
                <summary
                  className={mobileItemBase + " select-none cursor-pointer"}
                >
                  <span>Guides</span>
                  <svg
                    className="h-4 w-4 text-white/70 transition-transform group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <div className="mt-1 mb-2 mx-1 rounded-xl bg-black/30 border border-white/10 overflow-hidden">
                  <Link
                    href="/guides/best-weatherproof-extension-cords"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    Buying Guide
                  </Link>
                  <Link
                    href="/guides/rainblock-vs-twist-seal"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    Rain Block® vs Competitors
                  </Link>
                  <Link
                    href="/guides/how-to-weatherproof-christmas-lights-and-decorations"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    How-To Guide
                  </Link>
                  <Link
                    href="/guides/christmas-light-studio-instructions"
                    onClick={closeMobileMenu}
                    className={`${mobileSubItemBase} flex items-center gap-2`}
                  >
                    <Image
                      src="/sparkle.svg"
                      alt=""
                      width={16}
                      height={16}
                      aria-hidden
                    />
                    <span>Design Studio Instructions</span>
                  </Link>
                </div>
              </details>

              <div className={divider} />

              <div className={groupLabel}>Help & Policies</div>

              <details className="group rounded-xl">
                <summary
                  className={mobileItemBase + " select-none cursor-pointer"}
                >
                  <span>Help</span>
                  <svg
                    className="h-4 w-4 text-white/70 transition-transform group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <div className="mt-1 mb-2 mx-1 rounded-xl bg-black/30 border border-white/10 overflow-hidden">
                  <Link
                    href="/support"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    Support
                  </Link>
                  <Link
                    href="/contact"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    Contact Us
                  </Link>
                  <Link
                    href="/faq"
                    onClick={closeMobileMenu}
                    className={mobileSubItemBase}
                  >
                    FAQ
                  </Link>
                </div>
              </details>

              <Link
                href="/faq"
                onClick={closeMobileMenu}
                className={mobileItemBase}
              >
                FAQ
              </Link>

              <Link
                href="/policy"
                onClick={closeMobileMenu}
                className={mobileItemBase}
              >
                Policy
              </Link>

              <div className="h-2" />
            </div>
            <div className={`${glowLine} glow_sweep opacity-40`} />
          </div>
          </div>
        </dialog>
      )}
    </>
  );
}

